import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getRecordingStatus, recordingStreamUrl, getProctoringEvents, retryRecordingFinalize,
} from '../pages/ai-interview/utils/aiInterviewApi';
import { Icon } from './reportIcons';

/**
 * Report-side recording player for ONE kind (screen | camera). Lazy + range-streamed (nothing loads
 * until play), with a per-kind state machine (never an error — always a scenario), multi-segment
 * switching, and jump-to event markers (paste / silence / away / rejoin) aligned per segment.
 * Verification + the screen/camera split are separate sidebar sections in the modal.
 */

const EMPTY_COPY = {
  processing: { icon: 'clock', title: 'Recording is still processing', body: 'The video is being prepared — check back in a moment.' },
  failed: { icon: 'warning', title: 'Video conversion failed', body: 'The recording could not be converted. The original capture is preserved — you can retry the conversion.' },
  expired: { icon: 'archive', title: 'Recording expired', body: 'This recording was removed after its retention period.' },
  not_recorded: { icon: 'film', title: 'No recording for this session', body: 'This capture was not enabled, or nothing was recorded.' },
  unavailable: { icon: 'warning', title: 'Recording unavailable', body: 'The recording could not be located.' },
};

const EVENT_STYLE = {
  paste: { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500' },
  silence: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-300', dot: 'bg-gray-400' },
  away: { bg: 'bg-rose-50 dark:bg-rose-900/20', text: 'text-rose-700 dark:text-rose-300', dot: 'bg-rose-500' },
  rejoin: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', dot: 'bg-blue-500' },
};

const fmtClock = (sec) => {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return '';
  const s = Math.floor(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
};

const RecordingPlayer = ({ companyId, sessionId, candidateId, interviewId, kind = 'screen' }) => {
  const ready = companyId != null && sessionId && candidateId;
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [segIdx, setSegIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);   // lazy: video src only wired after "Load"
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);

  const handleRetry = async () => {
    if (retrying) return;
    setRetrying(true);
    setVideoError(false);
    try {
      const st = await retryRecordingFinalize(companyId, sessionId, candidateId, kind, interviewId);
      if (st) { setStatus(st); setSegIdx(0); setLoaded(false); }
    } catch { /* keep the failed state; the button stays available */ }
    finally { setRetrying(false); }
  };

  useEffect(() => {
    if (!ready) { setLoading(false); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [st, ev] = await Promise.all([
          getRecordingStatus(companyId, sessionId, candidateId, kind, interviewId).catch(() => null),
          getProctoringEvents(companyId, sessionId, candidateId, interviewId).catch(() => null),
        ]);
        if (!alive) return;
        setStatus(st || { state: 'unavailable', segments: [] });
        setEvents(ev?.events || []);
        setSegIdx(0);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [ready, companyId, sessionId, candidateId, interviewId, kind]);

  useEffect(() => { setLoaded(false); setVideoError(false); }, [segIdx, kind]);

  // While a segment is still being compressed (webm→mp4), re-check every few seconds so the card flips to
  // the ready video on its own — no manual reopen.
  useEffect(() => {
    if (!ready || status?.state !== 'processing') return undefined;
    const id = setInterval(async () => {
      const st = await getRecordingStatus(companyId, sessionId, candidateId, kind, interviewId).catch(() => null);
      if (st) setStatus(st);
    }, 6000);
    return () => clearInterval(id);
  }, [ready, status?.state, companyId, sessionId, candidateId, kind, interviewId]);

  const segments = status?.segments || [];
  const seg = segments[segIdx] || segments[0] || null;
  const overall = status?.state || 'not_recorded';
  const hasPlayable = seg && seg.playable;

  // Per-segment event offsets: offset = event.at − THIS segment's start, so markers line up on each part.
  const segEvents = useMemo(() => {
    if (!seg?.server_started_at) return [];
    const t0 = new Date(seg.server_started_at).getTime();
    const durSec = seg.duration_ms ? seg.duration_ms / 1000 : null;
    return (events || [])
      .map((e) => ({ ...e, offset: (new Date(e.at).getTime() - t0) / 1000 }))
      .filter((e) => Number.isFinite(e.offset) && e.offset >= -2 && (durSec == null || e.offset <= durSec + 5))
      .map((e) => ({ ...e, offset: Math.max(0, e.offset) }))
      .sort((a, b) => a.offset - b.offset);
  }, [events, seg]);

  const seekTo = (offset) => {
    if (!loaded) { setLoaded(true); return; }
    const v = videoRef.current;
    if (v) { try { v.currentTime = Math.max(0, offset); v.play?.().catch(() => {}); } catch { /* ignore */ } }
  };

  // MediaRecorder webm has NO duration in its header, so <video>.duration is Infinity and the scrubber
  // can't track or seek (the mp4 that ffmpeg produces has +faststart, so this only bites the raw webm
  // fallback — e.g. when ffmpeg isn't installed on the server). Force the browser to index the stream
  // by seeking to the end once; when the real duration is known, snap back to the start.
  const fixInfiniteDuration = () => {
    const v = videoRef.current;
    if (!v || Number.isFinite(v.duration)) return;
    const onDurationKnown = () => {
      if (!Number.isFinite(v.duration)) return;
      v.removeEventListener('durationchange', onDurationKnown);
      try { v.currentTime = 0; } catch { /* ignore */ }
    };
    v.addEventListener('durationchange', onDurationKnown);
    try { v.currentTime = 1e7; } catch { /* ignore */ }
  };

  if (!ready || loading) {
    return <p className="p-4 text-sm text-gray-400">{ready ? 'Loading recording…' : 'Recording unavailable for this interview.'}</p>;
  }

  const empty = EMPTY_COPY[overall] || EMPTY_COPY.not_recorded;

  return (
    <div>
      {/* Multi-segment switcher — the capture can restart (onboarding → interview, a re-share, a rejoin),
          so a kind can have several parts. Labelled neutrally by start time (NOT "rejoin"). */}
      {segments.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {segments.map((s, i) => (
            <button key={s.recording_id} type="button" onClick={() => setSegIdx(i)}
              className={`rounded-lg border px-2.5 py-1 text-xs font-medium transition ${i === segIdx ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300' : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'}`}>
              Recording {i + 1}{s.server_started_at && <span className="ml-1 text-gray-400">· {fmtTime(s.server_started_at)}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Conversion failed (or a playable file errored on load) — offer a retry that re-encodes from the
          preserved source capture. The raw capture, if any, is still playable below. */}
      {(seg?.state === 'failed' || overall === 'failed' || videoError) && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-800/60 dark:bg-amber-900/15">
          <span className="flex items-center gap-2 text-xs text-amber-800 dark:text-amber-200">
            <Icon name="warning" className="h-4 w-4 shrink-0" />
            Video conversion didn’t complete. The original capture is preserved.
          </span>
          <button type="button" onClick={handleRetry} disabled={retrying}
            className="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-amber-700 disabled:opacity-60">
            {retrying ? 'Retrying…' : 'Retry conversion'}
          </button>
        </div>
      )}

      {(seg?.state === 'processing' && !loaded) ? (
        <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-amber-300 bg-amber-50/40 text-center dark:border-amber-800/60 dark:bg-amber-900/10">
          <Icon name="clock" className="h-9 w-9 animate-pulse text-amber-500" />
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Compression in progress</span>
          <span className="max-w-xs text-xs text-gray-500 dark:text-gray-400">The recording is being converted and compressed — this usually takes under a minute. Reopen the report to check.</span>
          <button type="button" onClick={() => setLoaded(true)} className="mt-1 text-xs font-semibold text-primary-600 hover:underline dark:text-primary-400">Play the raw capture now</button>
        </div>
      ) : hasPlayable ? (
        loaded ? (
          <video key={`${seg.recording_id}-${seg.format || ''}`}
            ref={videoRef} controls autoPlay preload="metadata" onLoadedMetadata={fixInfiniteDuration}
            onError={() => setVideoError(true)}
            className="w-full rounded-xl bg-black" style={{ maxHeight: '65vh' }}
            src={recordingStreamUrl(companyId, sessionId, candidateId, kind, seg.recording_id)} />
        ) : (
          <button type="button" onClick={() => setLoaded(true)}
            className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 text-gray-500 transition hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/50 dark:hover:bg-gray-800">
            <span className="grid h-14 w-14 place-items-center rounded-full bg-primary-500 text-white shadow">
              <Icon name="play" className="h-7 w-7" />
            </span>
            <span className="text-sm font-semibold">Load recording</span>
            <span className="text-xs">Streams on demand — nothing downloads until you play.</span>
          </button>
        )
      ) : (
        <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 text-center dark:border-gray-700 dark:bg-gray-800/50">
          <Icon name={empty.icon} className="h-9 w-9 text-gray-400" />
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{empty.title}</span>
          <span className="max-w-xs text-xs text-gray-500 dark:text-gray-400">{empty.body}</span>
        </div>
      )}

      {/* Retention notice — when this video will be auto-removed (or that it's retained). */}
      {hasPlayable && (status?.keep_forever ? (
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-gray-400">
          <Icon name="verification" className="h-3.5 w-3.5" /> Retained — no automatic deletion.
        </p>
      ) : (seg?.expires_in_days != null && (
        <p className={`mt-2 flex items-center gap-1.5 text-[11px] ${seg.expires_in_days <= 3 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`}>
          <Icon name="clock" className="h-3.5 w-3.5" />
          {seg.expires_in_days === 0 ? 'This recording is removed today.' : `This recording will be removed in ${seg.expires_in_days} day${seg.expires_in_days === 1 ? '' : 's'}.`}
        </p>
      )))}

      {hasPlayable && segEvents.length > 0 && (
        <div className="mt-3">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Flagged moments — click to jump</p>
          <div className="flex flex-wrap gap-2">
            {segEvents.map((e, i) => {
              const st = EVENT_STYLE[e.type] || EVENT_STYLE.silence;
              return (
                <button key={`${e.type}-${i}`} type="button" onClick={() => seekTo(e.offset)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${st.bg} ${st.text}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
                  {e.label} · {fmtClock(e.offset)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordingPlayer;
